import { useRef, useState } from 'react';
import { Bell, Check, Mail, Lock, Monitor, Moon, Pencil, PlayCircle, Shield, Sparkles, Sun, Upload, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { type CountryCode } from 'libphonenumber-js';
import { useUserProfile } from '../context/UserProfileContext';
import { useTheme, type ThemePreference } from '../context/ThemeContext';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { PhoneInput, parseStoredPhone, phoneToE164 } from '../components/ui/PhoneInput';

// ── Helpers ─────────────────────────────────────────────────────────────────

function isPasswordProvider(providerIds: string[]): boolean {
  return providerIds.includes('password');
}

// ── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({
  photoBase64,
  displayName,
  size = 'lg',
}: {
  photoBase64?: string | null;
  displayName: string;
  size?: 'lg' | 'sm';
}) {
  const dim = size === 'lg' ? 'w-20 h-20' : 'w-12 h-12';
  const textSize = size === 'lg' ? 'text-3xl' : 'text-xl';

  if (photoBase64) {
    return (
      <img
        src={photoBase64}
        alt={displayName}
        className={`${dim} rounded-full object-cover border-2 border-stone bg-mist`}
      />
    );
  }

  return (
    <div
      className={`${dim} rounded-full bg-plum/10 flex items-center justify-center shrink-0`}
    >
      <span className={`font-display font-bold ${textSize} text-plum`}>
        {displayName.charAt(0).toUpperCase()}
      </span>
    </div>
  );
}

// ── Info row ─────────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-stone last:border-b-0">
      <span className="text-[13px] text-slate">{label}</span>
      <span className="text-[13px] font-semibold text-primary-theme">{value}</span>
    </div>
  );
}

// ── Edit profile form ────────────────────────────────────────────────────────

interface EditFormProps {
  initialDisplayName: string;
  initialPhotoBase64: string | null;
  initialPhoneCountry: CountryCode;
  initialPhoneNumber: string;
  onSave: (displayName: string, photoBase64: string | null, phone: string) => Promise<void>;
  onCancel: () => void;
}

function EditProfileForm({
  initialDisplayName,
  initialPhotoBase64,
  initialPhoneCountry,
  initialPhoneNumber,
  onSave,
  onCancel,
}: EditFormProps) {
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [photoBase64, setPhotoBase64] = useState<string | null>(initialPhotoBase64);
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>(initialPhoneCountry);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  function handlePhotoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoBase64(reader.result as string);
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!displayName.trim()) {
      setError('Display name is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const e164 = phoneNumber ? phoneToE164(phoneNumber, phoneCountry) : '';
      await onSave(displayName.trim(), photoBase64, e164);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      {error && (
        <div className="px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
          {error}
        </div>
      )}

      {/* Photo upload */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
          Profile Picture
        </p>
        <div className="flex items-center gap-4">
          {photoBase64 ? (
            <>
              <img
                src={photoBase64}
                alt="Profile"
                className="w-16 h-16 rounded-full object-cover border border-stone"
              />
              <div className="flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="text-[13px] text-plum font-semibold hover:text-rose transition-colors"
                >
                  Change photo
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPhotoBase64(null);
                    if (fileRef.current) fileRef.current.value = '';
                  }}
                  className="text-[13px] text-slate hover:text-rose transition-colors"
                >
                  Remove
                </button>
              </div>
            </>
          ) : (
            <label
              className="flex flex-col items-center justify-center w-16 h-16 border-2 border-dashed
                         border-stone rounded-full cursor-pointer bg-mist
                         hover:border-plum hover:bg-plum/5 transition-colors duration-150"
            >
              <Upload size={18} className="text-slate" />
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handlePhotoFile}
              />
            </label>
          )}
          {/* Hidden file input for "Change photo" button */}
          {photoBase64 && (
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={handlePhotoFile}
            />
          )}
        </div>
      </div>

      {/* Display name */}
      <div>
        <label
          htmlFor="profile-display-name"
          className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
        >
          Display Name <span className="text-rose">*</span>
        </label>
        <input
          id="profile-display-name"
          type="text"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          className="w-full h-11 px-3 rounded-lg border border-stone bg-surface
                     text-small text-primary-theme placeholder:text-slate
                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                     transition-colors duration-150"
        />
      </div>

      {/* Phone */}
      <div>
        <label
          htmlFor="profile-phone"
          className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
        >
          Phone <span className="text-slate font-normal normal-case tracking-normal">(optional)</span>
        </label>
        <PhoneInput
          id="profile-phone"
          country={phoneCountry}
          value={phoneNumber}
          onCountryChange={setPhoneCountry}
          onValueChange={setPhoneNumber}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 h-11 rounded-lg border border-stone text-small text-slate font-semibold
                     hover:border-midnight hover:text-primary-theme active:bg-mist
                     transition-colors duration-150"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 h-11 rounded-lg bg-plum text-white text-small font-semibold
                     hover:bg-rose active:opacity-80 disabled:opacity-50
                     transition-colors duration-150 flex items-center justify-center gap-2"
        >
          {saving ? <LoadingSpinner size="sm" /> : <><Check size={16} />Save</>}
        </button>
      </div>
    </form>
  );
}

// ── Email change form ────────────────────────────────────────────────────────

function EmailChangeSection({ currentEmail }: { currentEmail: string }) {
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmail.trim() || newEmail.trim() === currentEmail) {
      setError('Please enter a different email address.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api.post('/me/change-email', { newEmail: newEmail.trim() });
      setSuccess(true);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="px-4 py-3 rounded-lg bg-plum/5 border border-plum/20 text-[13px] text-primary-theme">
        Your email has been changed to <strong>{newEmail}</strong>. A confirmation has been sent to your previous address.
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl border border-stone bg-surface
                   text-[13px] font-semibold text-primary-theme
                   hover:border-plum/40 hover:shadow-brand-sm active:bg-mist
                   transition-all duration-150"
      >
        <Mail size={16} className="text-plum shrink-0" />
        Change Email Address
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-stone bg-surface px-4 py-4 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-semibold text-primary-theme">Change Email Address</p>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(''); setNewEmail(''); }}
          className="text-slate hover:text-primary-theme transition-colors"
          aria-label="Cancel"
        >
          <X size={16} />
        </button>
      </div>

      <p className="text-[13px] text-slate leading-snug">
        We'll send a verification link to your new email. Your address won't change until you click that link.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {error && (
          <p className="text-[13px] text-rose">{error}</p>
        )}
        <input
          type="email"
          placeholder="New email address"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          required
          className="w-full h-11 px-3 rounded-lg border border-stone bg-surface
                     text-small text-primary-theme placeholder:text-slate
                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                     transition-colors duration-150"
        />
        <button
          type="submit"
          disabled={submitting}
          className="h-11 rounded-lg bg-plum text-white text-small font-semibold
                     hover:bg-rose active:opacity-80 disabled:opacity-50
                     transition-colors duration-150 flex items-center justify-center gap-2"
        >
          {submitting ? <LoadingSpinner size="sm" /> : 'Send Verification Email'}
        </button>
      </form>
    </div>
  );
}

// ── Password change section ──────────────────────────────────────────────────

function PasswordChangeSection({ email }: { email: string }) {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  async function handleSend() {
    setError('');
    setSubmitting(true);
    try {
      await api.post('/auth/send-password-reset', { email });
      setSuccess(true);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="px-4 py-3 rounded-lg bg-plum/5 border border-plum/20 text-[13px] text-primary-theme">
        Password reset email sent to <strong>{email}</strong>. Check your inbox to set a new password.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {error && <p className="text-[13px] text-rose">{error}</p>}
      <button
        type="button"
        onClick={handleSend}
        disabled={submitting}
        className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl border border-stone bg-surface
                   text-[13px] font-semibold text-primary-theme
                   hover:border-plum/40 hover:shadow-brand-sm active:bg-mist disabled:opacity-50
                   transition-all duration-150"
      >
        {submitting
          ? <LoadingSpinner size="sm" />
          : <Lock size={16} className="text-plum shrink-0" />}
        Change Password
      </button>
    </div>
  );
}

// ── Theme toggle ────────────────────────────────────────────────────────────

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

function ThemeToggle() {
  const { preference, setPreference } = useTheme();

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
        Appearance
      </p>
      <div
        className="inline-flex rounded-xl p-1 gap-1"
        style={{ backgroundColor: 'var(--color-subtle)' }}
        role="radiogroup"
        aria-label="Theme preference"
      >
        {THEME_OPTIONS.map(({ value, label, icon: Icon }) => {
          const active = preference === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setPreference(value)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-semibold
                         transition-all duration-150 min-h-[44px]
                         ${active
                           ? 'shadow-brand-sm'
                           : 'hover:opacity-80'
                         }`}
              style={{
                backgroundColor: active ? 'var(--color-surface)' : 'transparent',
                color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              }}
            >
              <Icon size={16} />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MyProfilePage() {
  const navigate = useNavigate();
  const { currentUser, activeOrg, loading, error, refreshProfile } = useUserProfile();
  const [editing, setEditing] = useState(false);
  const [saveError, setSaveError] = useState('');

  const isPasswordAuth = isPasswordProvider(currentUser?.providerIds ?? []);

  async function handleSave(displayName: string, photoBase64: string | null, phone: string) {
    setSaveError('');
    await api.patch('/me/profile', { displayName, photoBase64, phone: phone || null });
    await refreshProfile();
    setEditing(false);
  }

  function handleCancelEdit() {
    setEditing(false);
    setSaveError('');
  }

  const primaryMembership = activeOrg ?? null;

  // Parse stored phone for display and edit initialization
  const { country: storedPhoneCountry, national: storedPhoneNational } = parseStoredPhone(
    currentUser?.phone,
  );

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-2xl md:mx-auto lg:max-w-5xl">
      <h1 className="font-display text-h1 text-primary-theme mb-1">My Profile</h1>
      <p className="text-[13px] text-slate mb-6">Your account information.</p>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load profile</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && currentUser && (
        <div className="flex flex-col lg:flex-row lg:gap-8">

          {/* ── Left column: profile info ────────────────────────── */}
          <div className="flex flex-col gap-6 lg:flex-1 lg:min-w-0">

            {/* ── Profile header ──────────────────────────────────── */}
            <div className="flex items-center gap-4">
              <Avatar
                photoBase64={currentUser.photoBase64}
                displayName={currentUser.displayName}
                size="lg"
              />
              <div className="flex-1 min-w-0">
                <p className="font-display text-h2 text-primary-theme leading-tight truncate">
                  {currentUser.displayName}
                </p>
                {currentUser.isPlatformAdmin && (
                  <span
                    className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full
                               bg-plum text-white text-[11px] font-bold uppercase tracking-wide"
                  >
                    <Shield size={10} />
                    Platform Admin
                  </span>
                )}
              </div>
              {!editing ? (
                <button
                  type="button"
                  onClick={() => { setEditing(true); setSaveError(''); }}
                  className="shrink-0 h-9 px-3 rounded-lg border border-stone text-slate text-[13px]
                             font-semibold flex items-center gap-1.5
                             hover:border-plum hover:text-plum active:bg-mist
                             transition-colors duration-150"
                >
                  <Pencil size={14} />
                  Edit
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="shrink-0 h-9 w-9 rounded-lg border border-stone text-slate
                             flex items-center justify-center
                             hover:border-midnight hover:text-primary-theme active:bg-mist
                             transition-colors duration-150"
                  aria-label="Cancel edit"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* ── Profile info or edit form ────────────────────────── */}
            {editing ? (
              <div className="bg-surface border border-stone rounded-xl px-4 py-5 shadow-brand-sm">
                {saveError && (
                  <div className="mb-4 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
                    {saveError}
                  </div>
                )}
                <EditProfileForm
                  initialDisplayName={currentUser.displayName}
                  initialPhotoBase64={currentUser.photoBase64 ?? null}
                  initialPhoneCountry={storedPhoneCountry}
                  initialPhoneNumber={storedPhoneNational}
                  onSave={handleSave}
                  onCancel={handleCancelEdit}
                />
              </div>
            ) : (
              <div className="bg-surface border border-stone rounded-xl px-4 shadow-brand-sm">
                <InfoRow label="Email" value={currentUser.email} />
                {currentUser.phone && (
                  <InfoRow label="Phone" value={storedPhoneNational || currentUser.phone} />
                )}
                {primaryMembership && (
                  <>
                    <InfoRow label="Organization" value={primaryMembership.orgName} />
                    <InfoRow
                      label="Role"
                      value={primaryMembership.role === 'admin' ? 'Admin' : primaryMembership.role === 'mover' ? 'Mover' : primaryMembership.role === 'dispatcher' ? 'Dispatcher' : primaryMembership.role === 'field_lead' ? 'Field Lead' : 'Estimator'}
                    />
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Right column: settings & actions ─────────────────── */}
          {!editing && (
            <div className="flex flex-col gap-6 mt-6 lg:mt-0 lg:w-80 lg:shrink-0">

              {/* ── Account security ──────────────────────────────── */}
              <div className="flex flex-col gap-3">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
                  Account Security
                </p>

                {isPasswordAuth ? (
                  <>
                    <EmailChangeSection currentEmail={currentUser.email} />
                    <PasswordChangeSection email={currentUser.email} />
                  </>
                ) : (
                  <div className="flex items-start gap-3 px-4 py-4 rounded-xl border border-stone bg-mist">
                    <div className="shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-surface border border-stone
                                    flex items-center justify-center">
                      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-primary-theme mb-0.5">
                        Signed in with Google
                      </p>
                      <p className="text-[13px] text-slate leading-snug">
                        Your email and password are managed by Google. To change them, visit your{' '}
                        <a
                          href="https://myaccount.google.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-plum underline hover:text-rose transition-colors"
                        >
                          Google Account settings
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Appearance toggle ─────────────────────────────── */}
              <ThemeToggle />

              {/* ── Email Notification Preferences ────────────────── */}
              <button
                type="button"
                onClick={() => navigate('/profile/notification-preferences')}
                className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl border border-stone bg-surface
                           text-[13px] font-semibold text-primary-theme
                           hover:border-plum/40 hover:shadow-brand-sm active:bg-mist
                           transition-all duration-150"
              >
                <Bell size={16} className="text-plum shrink-0" />
                Email Notification Preferences
              </button>

              {/* ── What's New ─────────────────────────────────────── */}
              <button
                type="button"
                onClick={() => navigate('/whats-new')}
                className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl border border-stone bg-surface
                           text-[13px] font-semibold text-primary-theme
                           hover:border-plum/40 hover:shadow-brand-sm active:bg-mist
                           transition-all duration-150"
              >
                <Sparkles size={16} className="text-plum shrink-0" />
                What&apos;s New
              </button>

              {/* ── Replay Tour (dispatchers only) ────────────────── */}
              {activeOrg?.role === 'dispatcher' && currentUser.onboardingCompletedAt && (
                <button
                  type="button"
                  onClick={async () => {
                    await api.patch('/me/profile', { onboardingCompletedAt: null, onboardingStep: 1 });
                    await refreshProfile();
                    navigate('/dispatch');
                  }}
                  className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl border border-stone bg-surface
                             text-[13px] font-semibold text-primary-theme
                             hover:border-plum/40 hover:shadow-brand-sm active:bg-mist
                             transition-all duration-150"
                >
                  <PlayCircle size={16} className="text-plum shrink-0" />
                  Replay Dispatch Tour
                </button>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
}
